import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, PlayCircle, BookOpen, Award, ChevronDown, Share2, Home } from 'lucide-react';
import PageShell from './PageShell';
import { RVP_CONTENT, RVP_LIST } from '../data/rendezvousPedagogiques';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/** Récupère ou crée un identifiant anonyme stable dans localStorage */
function getReviewerId() {
  try {
    const key = 'cv-reviewer-id';
    let id = localStorage.getItem(key);
    if (!id) {
      id = 'rv-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
      localStorage.setItem(key, id);
    }
    return id;
  } catch { return 'anon-' + Date.now(); }
}

/**
 * Vue « Session pédagogique · révision mobile ».
 * Rend tous les slides d'un RVP dans un flux vertical scrollable,
 * mobile-first, pour révision autonome à la maison (parents + élève).
 * Accessible via /seance/:slug (partagé par QR code ou SMS).
 */
export default function SessionEleveRvpPage() {
  const { slug } = useParams();
  const rvp = useMemo(() => RVP_LIST.find((r) => r.slug === slug), [slug]);
  const slides = useMemo(() => {
    const raw = RVP_CONTENT[slug] || [];
    // On filtre les slides non pertinents pour la révision autonome
    return raw.filter((s) => !['bienvenue-perso', 'merci-perso', 'qr-quiz'].includes(s?.type));
  }, [slug]);

  if (!rvp || slides.length === 0) {
    return (
      <PageShell>
        <section className="max-w-2xl mx-auto p-8 text-center">
          <h1 className="font-serif-editorial text-white text-[26px]">Séance introuvable</h1>
          <Link to="/" className="mt-6 inline-block px-5 h-11 leading-[44px] rounded-full bg-white/10 text-white text-[13px] font-semibold hover:bg-white/20">Retour à l&apos;accueil</Link>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="seance-page">
        {/* En-tête */}
        <nav className="flex items-center gap-2 text-[11.5px] text-white/50 font-mono-spec mb-4">
          <Link to="/" className="hover:text-white flex items-center gap-1"><Home size={11} /> codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Séance · {rvp.code}</span>
        </nav>

        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
          <BookOpen size={11} /> Révision · {rvp.code}
        </div>
        <h1 className="mt-3 font-serif-editorial text-white text-[30px] sm:text-[42px] leading-tight">{rvp.title}</h1>
        <p className="mt-3 text-white/70 text-[14.5px] max-w-2xl leading-relaxed">
          Retrouvez ici l&apos;intégralité du contenu de la séance : textes, questions, vidéos. Prenez le temps de le parcourir avec vos parents (ou votre enfant). Aucun compte requis.
        </p>

        {/* Barre de partage */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <ShareButton title={`Séance ${rvp.code} · Code Voiture`} />
          <Link to={`/quiz/rvp/${slug}`} className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 text-[13px] font-semibold transition">
            <Award size={13} /> Ne faire que le quiz
          </Link>
        </div>

        {/* Flux des slides */}
        <div className="mt-10 space-y-8">
          {slides.map((s, i) => <SlideBlock key={i} slide={s} index={i} />)}
        </div>

        {/* CTA fin + Marquer comme lu */}
        <MarkAsReadBlock slug={slug} rvpCode={rvp.code} />

        <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-950/15 p-6 text-center">
          <Award size={40} className="mx-auto text-emerald-300 mb-3" strokeWidth={1.3} />
          <div className="font-serif-editorial text-white text-[22px]">Bravo pour la révision !</div>
          <p className="mt-2 text-white/70 text-[13.5px] max-w-md mx-auto">Testez-vous en famille avec le quiz interactif — questions + explications détaillées.</p>
          <Link to={`/quiz/rvp/${slug}`} className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-bold transition">
            Faire le quiz maintenant →
          </Link>
        </div>
      </article>
    </PageShell>
  );
}

/* ══════════ Composants ══════════ */

function ShareButton({ title }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch { /* noop */ }
  };
  return (
    <button onClick={handleShare} data-testid="share-btn" className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-white/15 bg-white/5 text-white/85 hover:text-white hover:border-white/30 text-[13px] font-semibold transition">
      <Share2 size={13} /> {copied ? 'Lien copié ✓' : 'Partager'}
    </button>
  );
}

function SlideBlock({ slide }) {
  switch (slide.type) {
    case 'title':      return <TitleBlock slide={slide} />;
    case 'section':    return <SectionBlock slide={slide} />;
    case 'content':    return <ContentBlock slide={slide} />;
    case 'stat':       return <StatBlock slide={slide} />;
    case 'quote':      return <QuoteBlock slide={slide} />;
    case 'qcm':        return <QcmBlock slide={slide} />;
    case 'video':      return <VideoBlock slide={slide} />;
    case 'image':      return <ImageBlock slide={slide} />;
    case 'tour-de-table': return <TourDeTableBlock slide={slide} />;
    case 'objectif':   return <ObjectifBlock slide={slide} />;
    case 'bilan':      return <BilanBlock slide={slide} />;
    case 'message-sr': return <MessageSrBlock slide={slide} />;
    case 'cta':        return <CtaBlock slide={slide} />;
    case 'engagement': return <EngagementBlock slide={slide} />;
    default:           return null;
  }
}

function TitleBlock({ slide }) {
  return (
    <div className="py-6">
      {slide.kicker && <div className="text-red-400 font-mono-spec text-[11.5px] uppercase tracking-wider font-bold mb-3">{slide.kicker}</div>}
      <h2 className="font-serif-editorial text-white text-[28px] sm:text-[36px] leading-tight">{slide.title}</h2>
      {slide.subtitle && <p className="mt-3 text-white/70 text-[15px] leading-relaxed">{slide.subtitle}</p>}
    </div>
  );
}
function SectionBlock({ slide }) {
  return (
    <div className="border-l-4 border-red-500/60 pl-5 py-3 my-4">
      {slide.number && <div className="text-red-400 font-mono-spec text-[11px] uppercase tracking-wider font-bold">Bloc {slide.number}</div>}
      <div className="mt-1 font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">{slide.title}</div>
      {slide.note && <p className="mt-2 text-white/60 text-[13px]">{slide.note}</p>}
    </div>
  );
}
function ContentBlock({ slide }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      {slide.kicker && <div className="text-red-400 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold mb-2">{slide.kicker}</div>}
      <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] leading-tight">{slide.title}</h3>
      {slide.bullets && (
        <ul className="mt-4 space-y-2.5">
          {slide.bullets.map((b, i) => <li key={i} className="flex gap-2.5 items-start text-white/85 text-[14px] leading-relaxed"><span className="text-red-400 shrink-0 mt-0.5">▸</span><span>{b}</span></li>)}
        </ul>
      )}
      {slide.footnote && <p className="mt-4 text-white/50 text-[12.5px] italic border-l-2 border-white/15 pl-3">{slide.footnote}</p>}
    </div>
  );
}
function StatBlock({ slide }) {
  return (
    <div className="rounded-2xl border border-red-500/30 bg-red-950/15 p-6 text-center">
      <div className="font-serif-editorial text-white text-[64px] sm:text-[84px] leading-none">{slide.value}</div>
      {slide.label && <div className="mt-2 text-white text-[16px] sm:text-[18px] font-semibold">{slide.label}</div>}
      {slide.detail && <p className="mt-2 text-white/65 text-[13px] leading-relaxed max-w-xl mx-auto">{slide.detail}</p>}
      {slide.source && <div className="mt-3 text-white/40 font-mono-spec text-[11px] uppercase tracking-wider">Source : {slide.source}</div>}
    </div>
  );
}
function QuoteBlock({ slide }) {
  return (
    <blockquote className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 relative">
      <div className="text-red-500/40 font-serif-editorial text-[52px] leading-none">&ldquo;</div>
      <p className="mt-2 font-serif-editorial text-white text-[18px] sm:text-[22px] leading-snug italic">{slide.quote}</p>
      {slide.author && <div className="mt-3 text-white/60 text-[12.5px] font-mono-spec uppercase tracking-wider">— {slide.author}</div>}
    </blockquote>
  );
}
function ImageBlock({ slide }) {
  if (!slide.src) return null;
  return (
    <figure className="rounded-2xl overflow-hidden border border-white/10">
      <img src={slide.src} alt={slide.alt || ''} className="w-full h-auto" loading="lazy" />
      {slide.caption && <figcaption className="p-3 text-white/60 text-[12.5px] italic text-center bg-black/40">{slide.caption}</figcaption>}
    </figure>
  );
}
function VideoBlock({ slide }) {
  if (!slide.youtube_id) return null;
  return (
    <div className="rounded-2xl overflow-hidden border border-fuchsia-500/30 bg-fuchsia-950/10">
      <div className="p-3 flex items-center gap-2 border-b border-white/5">
        <PlayCircle size={16} className="text-fuchsia-300" />
        <div className="text-fuchsia-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Vidéo {slide.title ? `· ${slide.title}` : ''}</div>
      </div>
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${slide.youtube_id}`}
          title={slide.title || 'Vidéo'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {slide.note && <div className="p-3 text-white/70 text-[13px] italic">{slide.note}</div>}
    </div>
  );
}
function QcmBlock({ slide }) {
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [showExpl, setShowExpl] = useState(false);
  const submit = () => { if (selected !== null) { setRevealed(true); setShowExpl(true); } };
  const reset = () => { setSelected(null); setRevealed(false); setShowExpl(false); };
  return (
    <div className="rounded-2xl border border-red-500/30 bg-white/[0.02] p-5" data-testid="seance-qcm">
      <div className="text-red-400 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold mb-2">Question interactive</div>
      <div className="font-serif-editorial text-white text-[18px] sm:text-[20px] leading-snug">{slide.question}</div>
      <div className="mt-4 space-y-2">
        {slide.options.map((o, i) => {
          const isSel = selected === i;
          const showCorrect = revealed && o.correct;
          const showWrong = revealed && isSel && !o.correct;
          const cls = showCorrect ? 'border-emerald-500/60 bg-emerald-950/30 text-emerald-100'
            : showWrong ? 'border-red-500/60 bg-red-950/30 text-red-100'
            : isSel ? 'border-white/40 bg-white/10 text-white'
            : 'border-white/10 bg-white/[0.02] text-white/80';
          const Icon = showCorrect ? CheckCircle2 : showWrong ? XCircle : null;
          return (
            <button key={i} disabled={revealed} onClick={() => setSelected(i)} className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-center gap-3 ${cls} ${!revealed ? 'hover:border-white/30 cursor-pointer' : 'cursor-default'}`}>
              <span className="shrink-0 w-6 h-6 rounded-full border border-white/30 flex items-center justify-center text-[11px] font-mono-spec">{String.fromCharCode(65 + i)}</span>
              <span className="flex-1 text-[13.5px] leading-snug">{o.text}</span>
              {Icon && <Icon size={16} className="shrink-0" />}
            </button>
          );
        })}
      </div>
      {!revealed && (
        <button onClick={submit} disabled={selected === null} className="mt-4 w-full h-11 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white text-[13px] font-bold transition">Valider ma réponse</button>
      )}
      {revealed && slide.explanation && showExpl && (
        <div className="mt-4 p-4 rounded-xl border border-amber-500/25 bg-amber-950/15">
          <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-1">Explication</div>
          <p className="text-white/85 text-[13.5px] leading-relaxed">{slide.explanation}</p>
          <button onClick={reset} className="mt-3 text-white/60 hover:text-white text-[12px] underline">Recommencer</button>
        </div>
      )}
    </div>
  );
}
function TourDeTableBlock({ slide }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-sky-500/30 bg-sky-950/10 p-5">
      <div className="text-sky-300 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold mb-2">Tour de table · en famille</div>
      <div className="font-serif-editorial text-white text-[18px] sm:text-[20px] leading-tight">{slide.title}</div>
      {slide.intro && <p className="mt-3 text-white/75 text-[14px] leading-relaxed">{slide.intro}</p>}
      <button onClick={() => setOpen((o) => !o)} className="mt-4 flex items-center gap-2 text-sky-300 hover:text-sky-200 text-[13px] font-semibold">
        {open ? 'Masquer' : 'Voir'} les {slide.questions.length} questions à échanger <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ol className="mt-3 space-y-2 pl-5 list-decimal text-white/80 text-[13.5px] leading-relaxed">
          {slide.questions.map((q, i) => <li key={i}>{q}</li>)}
        </ol>
      )}
      {slide.note && <p className="mt-4 text-white/55 text-[12.5px] italic border-l-2 border-sky-500/30 pl-3">{slide.note}</p>}
    </div>
  );
}
function ObjectifBlock({ slide }) {
  return (
    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-950/15 p-5">
      <div className="text-emerald-300 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold mb-2">Objectif de la séance</div>
      <p className="text-white text-[15px] sm:text-[16px] leading-relaxed">{slide.objectif}</p>
    </div>
  );
}
function BilanBlock({ slide }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="text-white/60 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold mb-2">Bilan</div>
      {slide.title && <div className="font-serif-editorial text-white text-[20px] mb-3">{slide.title}</div>}
      {slide.acquis && (
        <div className="mb-4">
          <div className="text-emerald-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold mb-1.5">Acquis</div>
          <ul className="space-y-1 text-white/80 text-[13.5px]">{slide.acquis.map((a, i) => <li key={i}>✓ {a}</li>)}</ul>
        </div>
      )}
      {slide.a_travailler && (
        <div>
          <div className="text-amber-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold mb-1.5">À travailler</div>
          <ul className="space-y-1 text-white/80 text-[13.5px]">{slide.a_travailler.map((a, i) => <li key={i}>◇ {a}</li>)}</ul>
        </div>
      )}
    </div>
  );
}
function MessageSrBlock({ slide }) {
  return (
    <div className="rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-950/30 via-[#0d0d0e] to-[#0a0a0b] p-6 text-center">
      {slide.kicker && <div className="text-red-300 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold mb-3">{slide.kicker}</div>}
      <p className="font-serif-editorial text-white text-[22px] sm:text-[28px] leading-tight">{slide.message}</p>
      {slide.detail && <p className="mt-4 text-white/75 text-[14px] leading-relaxed max-w-xl mx-auto">{slide.detail}</p>}
    </div>
  );
}
function CtaBlock({ slide }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      {slide.title && <div className="font-serif-editorial text-white text-[20px]">{slide.title}</div>}
      {slide.description && <p className="mt-2 text-white/70 text-[13.5px] leading-relaxed">{slide.description}</p>}
      {slide.next_action && <div className="mt-3 text-red-300 text-[12.5px] font-mono-spec uppercase tracking-wider font-bold">→ {slide.next_action}</div>}
    </div>
  );
}
function EngagementBlock({ slide }) {
  return (
    <div className="rounded-2xl border border-fuchsia-500/40 bg-fuchsia-950/15 p-6">
      <div className="text-fuchsia-300 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold mb-2">Engagement</div>
      <p className="font-serif-editorial text-white text-[18px] sm:text-[20px] leading-tight">{slide.commitment || slide.title}</p>
      {slide.detail && <p className="mt-3 text-white/70 text-[13.5px] leading-relaxed">{slide.detail}</p>}
    </div>
  );
}

function MarkAsReadBlock({ slug, rvpCode }) {
  const [state, setState] = useState(() => {
    try { return localStorage.getItem(`cv-read-${slug}`) === '1' ? 'done' : 'idle'; } catch { return 'idle'; }
  });
  const [role, setRole] = useState('parent');
  const [error, setError] = useState('');

  const submit = async () => {
    setState('loading');
    setError('');
    try {
      await axios.post(`${API}/api/seance-reviews`, {
        slug,
        reviewer_id: getReviewerId(),
        role,
      });
      try { localStorage.setItem(`cv-read-${slug}`, '1'); } catch { /* noop */ }
      setState('done');
    } catch (e) {
      setError('Impossible d\'enregistrer. Réessayez dans quelques instants.');
      setState('idle');
    }
  };

  if (state === 'done') {
    return (
      <div className="mt-14 rounded-2xl border border-emerald-500/50 bg-emerald-950/30 p-6 text-center" data-testid="mark-read-done">
        <CheckCircle2 size={40} className="mx-auto text-emerald-300 mb-3" strokeWidth={1.3} />
        <div className="font-serif-editorial text-white text-[22px]">Merci ! Votre lecture est enregistrée</div>
        <p className="mt-2 text-white/70 text-[13.5px] max-w-md mx-auto">Votre formateur sait maintenant que vous avez révisé la séance {rvpCode}. Bravo pour votre engagement 👏</p>
      </div>
    );
  }

  return (
    <div className="mt-14 rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-950/25 via-[#0d0d0e] to-[#0a0a0b] p-6" data-testid="mark-read-block">
      <div className="text-red-300 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold mb-2">📩 Envoyez un signal à votre formateur</div>
      <h3 className="font-serif-editorial text-white text-[22px] leading-tight">Marquer la séance comme lue</h3>
      <p className="mt-2 text-white/70 text-[13.5px] leading-relaxed">
        En un clic, indiquez à votre formateur que vous avez pris le temps de revoir la séance {rvpCode} à la maison. C&apos;est anonyme et volontaire — cela lui permet d&apos;adapter le prochain RVP.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-white/50 text-[11.5px] font-mono-spec uppercase tracking-wider mr-1">Je suis :</span>
        {['parent', 'eleve', 'autre'].map((r) => (
          <button key={r} onClick={() => setRole(r)} data-testid={`role-${r}`} className={`h-9 px-3 rounded-full border text-[12px] font-semibold transition ${role === r ? 'border-red-500/60 bg-red-500/15 text-red-100' : 'border-white/15 text-white/60 hover:text-white'}`}>
            {r === 'eleve' ? 'Élève' : r === 'parent' ? 'Parent / accompagnateur' : 'Autre'}
          </button>
        ))}
      </div>
      <button
        onClick={submit}
        disabled={state === 'loading'}
        data-testid="mark-read-btn"
        className="mt-5 w-full h-12 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[14px] font-bold transition inline-flex items-center justify-center gap-2"
      >
        {state === 'loading' ? 'Envoi…' : <>✅ J&apos;ai lu la séance en entier</>}
      </button>
      {error && <div className="mt-3 text-red-300 text-[12.5px] text-center">{error}</div>}
    </div>
  );
}
